/* @flow */
import * as React from 'react';
import { translate } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';
import RequireForAccess from 'react-access';
import ListItem from '@material-ui/core/ListItem';

import type { RoleList } from 'permissions/defs';
import type ActionDrawerStore, { ExportTypes } from 'stores/ActionDrawerStore';
import ActionButton from './components/ActionButton/ActionButton';
import styles from './ActionsList.module.scss';

export type Action = {
  label: string,
  icon: string,
  class: string,
  action: string,
  permissions: RoleList,
  condition?: (data: any[], action: Action, callback: Function) => React.Node,
};

type TypeProps = {|
  actions: Object,
  data: Object,
  actionDrawerStore: ActionDrawerStore,
  singleAction: boolean,
  disabled: boolean,
  accordionList: boolean,
  tableHeader: boolean,
  onActionCompleted?: Function,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore')
@observer
class ActionsList extends React.Component<*> {
  handleActionDrawer = (currentAction: string, data: Object) => {
    const LOCATION_COLUMN_SITE = 1;
    const LOCATION_COLUMN_BUILDING = 2;
    const actionDrawerStore: ActionDrawerStore = this.props.actionDrawerStore;

    // When we call action form in <Account Manage Locations>
    // in order to reload the specific column that we working on
    if (this.props.locationColumn) {
      actionDrawerStore.locationColumn =
        this.props.locationColumn > LOCATION_COLUMN_BUILDING
          ? this.props.locationColumn - 1
          : LOCATION_COLUMN_SITE;
    }

    actionDrawerStore.openActionDrawer(currentAction, data, this.props.onActionCompleted);
  };

  async handleActionDownload(type: ExportTypes, loadingKey: string): Promise<void> {
    const { data, actionDrawerStore }: TypeProps = this.props;
    actionDrawerStore.setLoadingEntry(loadingKey, true);
    await actionDrawerStore.exportAssetData(data.id, type);
    actionDrawerStore.setLoadingEntry(loadingKey, false);
  }

  // TODO: perhaps consider using react-access consumer to evaluate on permissions ??
  actionEvaluate = (action: Object) => {
    const { data, t }: TypeProps = this.props;
    const actionValid: * =
      typeof action.condition === 'function'
        ? action.condition(data, action, this.handleActionDrawer, t)
        : !action.condition && true;
    return actionValid;
  };

  combinedPermissions = () => {
    const { actions }: TypeProps = this.props;

    const combined = actions
      .map((action, index) => action.permissions)
      .reduce(function(accumulator, currentValue) {
        return accumulator.concat(currentValue);
      }, [])
      .filter((v, i, a) => a.indexOf(v) === i);
    return combined;
  };

  render() {
    const {
      actions,
      data,
      t,
      singleAction,
      disabled,
      accordionList,
      tableHeader,
    }: TypeProps = this.props;

    if (singleAction) {
      return (
        this.actionEvaluate(actions) && (
          <RequireForAccess permissions={actions.permissions} key={actions.label}>
            {typeof actions.condition === 'function' ? (
              actions.condition(data, actions, this.handleActionDrawer, t)
            ) : (
              <ActionButton
                action={actions}
                data={data}
                disabled={disabled}
                onActionDrawer={this.handleActionDrawer}
                onActionDownload={this.handleActionDownload.bind(this)}
              />
            )}
          </RequireForAccess>
        )
      );
    }

    if (accordionList) {
      return (
        <RequireForAccess permissions={this.combinedPermissions()}>
          {actions &&
            actions.map(
              (action: any) =>
                this.actionEvaluate(action) && (
                  <RequireForAccess permissions={action.permissions} key={action.label || 'empty'}>
                    <ListItem>
                      {typeof action.condition === 'function' ? (
                        action.condition(data, action, this.handleActionDrawer, t)
                      ) : (
                        <ActionButton
                          action={action}
                          data={data}
                          onActionDrawer={this.handleActionDrawer}
                          onActionDownload={this.handleActionDownload.bind(this)}
                        />
                      )}
                    </ListItem>
                  </RequireForAccess>
                )
            )}
        </RequireForAccess>
      );
    }

    if (tableHeader) {
      return (
        <span className={styles['action-buttons']}>
          {actions &&
            actions.map(
              (action: any) =>
                this.actionEvaluate(action) && (
                  <RequireForAccess permissions={action.permissions} key={action.label || 'empty'}>
                    <span className={styles['action-button']}>
                      {typeof action.condition === 'function' ? (
                        action.condition(data, action, this.handleActionDrawer, t)
                      ) : (
                        <ActionButton
                          action={action}
                          data={data}
                          onActionDrawer={this.handleActionDrawer}
                          onActionDownload={this.handleActionDownload.bind(this)}
                        />
                      )}
                    </span>
                  </RequireForAccess>
                )
            )}
        </span>
      );
    }

    return (
      <RequireForAccess permissions={this.combinedPermissions()}>
        <div className={styles['actions-list']}>
          <h3>{t('general:action_plural')}</h3>
          <ul>
            {actions &&
              actions.map(
                (action: any, index: number) =>
                  this.actionEvaluate(action) && (
                    <RequireForAccess permissions={action.permissions} key={index.toString()}>
                      <li>
                        {typeof action.condition === 'function' ? (
                          action.condition(
                            data,
                            action,
                            this.handleActionDrawer,
                            this.handleActionDownload.bind(this)
                          )
                        ) : (
                          <ActionButton
                            action={action}
                            data={data}
                            disabled={disabled}
                            onActionDrawer={this.handleActionDrawer}
                            onActionDownload={this.handleActionDownload.bind(this)}
                          />
                        )}
                      </li>
                    </RequireForAccess>
                  )
              )}
          </ul>
        </div>
      </RequireForAccess>
    );
  }
}

export default ActionsList;
